import React, { useState, useRef, useEffect } from 'react';
import { Box, Container, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, Paper, Grid, Alert, LinearProgress, Chip } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';
import { CheckCircle, Cancel, Add } from '@mui/icons-material';
import WordSearchGenerator from './WordSearchGenerator';

// Hardcoded Secret Key
const PLAYFAB_SECRET_KEY = 'HXG9K1QJGADP491W5IIZRQDQZKBRYSCBKG8DXHOQ9CCNQ5T1XQ';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

const OutputBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  fontFamily: 'monospace',
  fontSize: '14px',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  maxHeight: '400px',
  overflowY: 'auto',
}));

const GridDisplay = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  fontFamily: 'Courier New, monospace',
  fontSize: '16px',
  lineHeight: '1.8',
  overflowX: 'auto',
  whiteSpace: 'pre',
  letterSpacing: '0.5em',
}));

function GeneratorPage() {
  const [wordsInput, setWordsInput] = useState('');
  const [secretInput, setSecretInput] = useState('GRADUATE');
  const [gridSize, setGridSize] = useState(12);
  const [difficulty, setDifficulty] = useState(3);
  const [dateInput, setDateInput] = useState(new Date());
  const [headlineInput, setHeadlineInput] = useState('');
  const [jokeInput, setJokeInput] = useState('');
  
  const [keyText, setKeyText] = useState('');
  const [dataText, setDataText] = useState('');
  const [gridDisplay, setGridDisplay] = useState('');
  const [error, setError] = useState('');
  const [puzzleData, setPuzzleData] = useState(null);
  const canvasRef = useRef(null);
  
  // PlayFab upload state
  const [generatedPuzzleData, setGeneratedPuzzleData] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadTags, setUploadTags] = useState(['untested']);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ type: '', text: '' });
  const [uploadSteps, setUploadSteps] = useState([
    { label: 'Prepare JSON', status: 'pending', message: '' },
    { label: 'Upload to PlayFab', status: 'pending', message: '' }
  ]);
  const [showUploadSteps, setShowUploadSteps] = useState(false);

  const formatDateForPuzzle = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const handleGenerate = () => {
    setError('');
    
    if (!wordsInput.trim()) {
      setError('Please enter comma-separated words');
      return;
    }
    
    if (!secretInput.trim()) {
      setError('Please enter a secret word');
      return;
    }
    
    if (!dateInput || !(dateInput instanceof Date) || isNaN(dateInput)) {
      setError('Please select a valid date');
      return;
    }
    
    const formattedDate = formatDateForPuzzle(dateInput);
    
    setKeyText('Generating puzzle...');
    setDataText('');
    setGridDisplay('');
    
    try {
      const result = WordSearchGenerator.generatePuzzle(
        wordsInput.trim(),
        secretInput.trim(),
        gridSize,
        difficulty
      );
      
      if (!result) {
        setError('Failed to generate puzzle. Try reducing the number of words or increasing grid size.');
        setKeyText('');
        return;
      }
      
      // Update result with custom date, headline, and joke
      result.data.date = formattedDate;
      if (headlineInput.trim()) {
        result.data.headline = headlineInput.trim();
      }
      if (jokeInput.trim()) {
        result.data.joke = jokeInput.trim();
      }
      result.key = `wordsearch_${formattedDate}`;
      result.fullJson = { [result.key]: result.data };
      
      setKeyText(result.key);
      setDataText(JSON.stringify(result.data, null, 2));
      
      // Combine regular words with secret word for visualization
      const allWords = [...result.data.words];
      if (result.data.secret) {
        allWords.push(result.data.secret);
      }
      
      setPuzzleData({
        puzzle: result.data.puzzle,
        size: gridSize,
        words: allWords,
        joke: result.data.joke
      });
      
      // Store generated puzzle data for upload
      setGeneratedPuzzleData(result.data);
      setUploadTitle(formattedDate);
      
      displayGridVisualization(result.data.puzzle, gridSize, allWords, result.data.joke);
    } catch (err) {
      setError('Error generating puzzle: ' + err.message);
      setKeyText('');
    }
  };

  const displayGridVisualization = (puzzleString, size, words, joke) => {
    let gridText = 'Words placed:\n';
    for (const wordInfo of words) {
      gridText += '• ' + wordInfo + '\n';
    }
    
    if (joke) {
      gridText += '\nJoke: ' + joke;
    }
    
    setGridDisplay(gridText);
  };

  useEffect(() => {
    if (puzzleData && canvasRef.current) {
      drawPuzzleOnCanvas(puzzleData);
    }
  }, [puzzleData]);

  const drawPuzzleOnCanvas = (data) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const { puzzle, size, words } = data;
    
    const cellSize = 40;
    const padding = 20;
    const canvasSize = size * cellSize + padding * 2;
    
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    
    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    
    // Parse word placements
    const wordPlacements = words.map(wordInfo => {
      const parts = wordInfo.split(',');
      return {
        word: parts[0],
        startIndex: parseInt(parts[1]),
        endIndex: parseInt(parts[2])
      };
    });
    
    // Draw grid lines
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    for (let i = 0; i <= size; i++) {
      ctx.beginPath();
      ctx.moveTo(padding, padding + i * cellSize);
      ctx.lineTo(padding + size * cellSize, padding + i * cellSize);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(padding + i * cellSize, padding);
      ctx.lineTo(padding + i * cellSize, padding + size * cellSize);
      ctx.stroke();
    }
    
    // Draw letters
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const index = y * size + x;
        if (index < puzzle.length) {
          const char = puzzle[index];
          const centerX = padding + x * cellSize + cellSize / 2;
          const centerY = padding + y * cellSize + cellSize / 2;
          ctx.fillText(char, centerX, centerY);
        }
      }
    }
    
    // Draw lines for each word
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];
    
    wordPlacements.forEach((placement, index) => {
      const startX = placement.startIndex % size;
      const startY = Math.floor(placement.startIndex / size);
      const endX = placement.endIndex % size;
      const endY = Math.floor(placement.endIndex / size);
      
      const startCenterX = padding + startX * cellSize + cellSize / 2;
      const startCenterY = padding + startY * cellSize + cellSize / 2;
      const endCenterX = padding + endX * cellSize + cellSize / 2;
      const endCenterY = padding + endY * cellSize + cellSize / 2;
      
      ctx.strokeStyle = colors[index % colors.length];
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      
      ctx.beginPath();
      ctx.moveTo(startCenterX, startCenterY);
      ctx.lineTo(endCenterX, endCenterY);
      ctx.stroke();
      
      // Draw circles at start and end
      ctx.fillStyle = colors[index % colors.length];
      ctx.globalAlpha = 0.3;
      ctx.beginPath();
      ctx.arc(startCenterX, startCenterY, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(endCenterX, endCenterY, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // PlayFab upload functions
  const handleAddTag = () => {
    if (tagInput.trim() && !uploadTags.includes(tagInput.trim())) {
      setUploadTags([...uploadTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setUploadTags(uploadTags.filter(tag => tag !== tagToDelete));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const fetchEntityToken = async (titleId) => {
    try {
      const response = await fetch(`https://${titleId}.playfabapi.com/Authentication/GetEntityToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-SecretKey': PLAYFAB_SECRET_KEY
        },
        body: JSON.stringify({})
      });

      const data = await response.json();
      
      if (data.data && data.data.EntityToken) {
        return data.data.EntityToken;
      } else {
        throw new Error(data.errorMessage || 'Failed to get entity token');
      }
    } catch (error) {
      throw new Error(`Failed to fetch entity token: ${error.message}`);
    }
  };

  const updateUploadStep = (stepIndex, status, message) => {
    setUploadSteps(prev => prev.map((step, idx) => 
      idx === stepIndex ? { ...step, status, message } : step
    ));
  };

  const handleUploadToPlayFab = async () => {
    if (!generatedPuzzleData || !uploadTitle) {
      setUploadMessage({ type: 'error', text: 'Please generate a puzzle first' });
      return;
    }

    setUploading(true);
    setShowUploadSteps(true);
    setUploadMessage({ type: 'info', text: 'Starting upload process...' });
    
    // Reset steps
    setUploadSteps([
      { label: 'Prepare JSON', status: 'pending', message: '' },
      { label: 'Upload to PlayFab', status: 'pending', message: '' }
    ]);

    try {
      // Step 1: Prepare JSON
      updateUploadStep(0, 'loading', 'Formatting catalog item JSON...');
      
      // Auto-generate Item ID from title
      const itemId = uploadTitle.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      
      const catalogItem = {
        Item: {
          Id: itemId,
          Type: "catalogItem",
          Title: {
            NEUTRAL: uploadTitle
          },
          ContentType: 'daily-puzzle-secret-word',
          Tags: uploadTags,
          DisplayProperties: generatedPuzzleData
        }
      };
      
      console.log('Prepared JSON:', JSON.stringify(catalogItem, null, 2));
      console.log('Auto-generated Item ID:', itemId);
      updateUploadStep(0, 'success', `JSON formatted successfully (ID: ${itemId})`);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Fetch entity token
      setUploadMessage({ type: 'info', text: 'Fetching entity token...' });
      const token = await fetchEntityToken('1219E1');
      
      // Step 2: Upload to PlayFab
      updateUploadStep(1, 'loading', 'Uploading to PlayFab Catalog V2...');
      
      const uploadResponse = await fetch(`https://1219E1.playfabapi.com/Catalog/CreateDraftItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-EntityToken': token
        },
        body: JSON.stringify(catalogItem)
      });

      const uploadData = await uploadResponse.json();
      console.log('Upload response:', uploadData);
      
      if (uploadData.code === 200 || uploadData.status === 'OK' || !uploadData.error) {
        updateUploadStep(1, 'success', `Server response: ${uploadData.status || 'Item created successfully'}`);
        setUploadMessage({ 
          type: 'success', 
          text: `Item "${itemId}" uploaded successfully to PlayFab! Check Game Manager to publish.` 
        });
      } else {
        throw new Error(uploadData.errorMessage || uploadData.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // Mark current step as failed
      const currentStep = uploadSteps.findIndex(s => s.status === 'loading');
      if (currentStep !== -1) {
        updateUploadStep(currentStep, 'error', error.message);
      }
      
      setUploadMessage({ 
        type: 'error', 
        text: `Error: ${error.message}` 
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h3" component="h1" align="center" sx={{ color: 'white', mb: 4, fontWeight: 700 }}>
          Word Search Generator
        </Typography>
        
        <StyledPaper elevation={3}>
          <Typography variant="h5" gutterBottom>Input</Typography>
          
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Words (separated by spaces, commas, tabs, or any punctuation)"
              value={wordsInput}
              onChange={(e) => setWordsInput(e.target.value)}
              placeholder="HARVARD DUKE PRINCETON BROWN STANFORD"
              variant="outlined"
            />
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Secret Word"
                value={secretInput}
                onChange={(e) => setSecretInput(e.target.value)}
                placeholder="GRADUATE"
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date"
                  value={dateInput}
                  onChange={(newValue) => setDateInput(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Grid Size"
                value={gridSize}
                onChange={(e) => setGridSize(parseInt(e.target.value))}
                inputProps={{ min: 8, max: 30 }}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  label="Difficulty"
                >
                  <MenuItem value={0}>Easy (0) - Horizontal/Vertical only</MenuItem>
                  <MenuItem value={1}>Normal (1) - Add Diagonals</MenuItem>
                  <MenuItem value={2}>Hard (2) - Add Backwards</MenuItem>
                  <MenuItem value={3}>Expert (3) - All directions + Smart fill</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Headline (optional)"
              value={headlineInput}
              onChange={(e) => setHeadlineInput(e.target.value)}
              placeholder="Enter a headline for the puzzle"
              variant="outlined"
            />
          </Box>
          
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Joke (optional)"
              value={jokeInput}
              onChange={(e) => setJokeInput(e.target.value)}
              placeholder="Enter a joke for the puzzle"
              variant="outlined"
            />
          </Box>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGenerate}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                }
              }}
            >
              Generate Puzzle
            </Button>
          </Box>
          
          {error && (
            <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
              {error}
            </Typography>
          )}
        </StyledPaper>
        
        {keyText && !error && (
          <>
            <StyledPaper elevation={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Puzzle Key</Typography>
                <Button size="small" onClick={() => copyToClipboard(keyText)}>Copy</Button>
              </Box>
              <OutputBox>{keyText}</OutputBox>
            </StyledPaper>
            
            <StyledPaper elevation={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">JSON Data</Typography>
                <Button size="small" onClick={() => copyToClipboard(dataText)}>Copy</Button>
              </Box>
              <OutputBox>{dataText}</OutputBox>
            </StyledPaper>
            
            <StyledPaper elevation={3}>
              <Typography variant="h6" gutterBottom>Puzzle Visualization with Solution</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <canvas ref={canvasRef} style={{ border: '1px solid #ddd', borderRadius: '4px' }} />
              </Box>
              <GridDisplay>{gridDisplay}</GridDisplay>
            </StyledPaper>

            {/* PlayFab Upload Section */}
            <StyledPaper elevation={3}>
              <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>Upload to PlayFab</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Upload this puzzle to PlayFab Catalog V2</Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Title Field */}
                <TextField
                  label="Title"
                  variant="outlined"
                  fullWidth
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  helperText="Item ID will be auto-generated from this title"
                />
                
                {/* Display Properties (Read-only) */}
                <TextField
                  label="Display Properties (Auto-filled from puzzle)"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={6}
                  value={generatedPuzzleData ? JSON.stringify(generatedPuzzleData, null, 2) : ''}
                  InputProps={{ readOnly: true }}
                  helperText="This is automatically populated from the generated puzzle"
                />
                
                {/* Tags Section */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom>Tags</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagInputKeyPress}
                      placeholder="Enter a tag and press Enter"
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddTag}
                      startIcon={<Add />}
                      sx={{ minWidth: '100px' }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {uploadTags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        onDelete={() => handleDeleteTag(tag)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
                
                {/* Upload Button */}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleUploadToPlayFab}
                  disabled={uploading || !generatedPuzzleData}
                  sx={{ py: 1.5 }}
                >
                  {uploading ? 'Uploading...' : 'Upload to PlayFab'}
                </Button>
                
                {uploading && <LinearProgress />}

                {/* Upload Progress */}
                {showUploadSteps && (
                  <Paper elevation={2} sx={{ p: 3, bgcolor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom>Upload Progress</Typography>
                    
                    {uploadMessage.text && (
                      <Alert severity={uploadMessage.type || 'info'} sx={{ mb: 2 }}>
                        {uploadMessage.text}
                      </Alert>
                    )}
                    
                    {uploadSteps.map((step, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          {step.status === 'success' && <CheckCircle color="success" />}
                          {step.status === 'error' && <Cancel color="error" />}
                          {step.status === 'loading' && <LinearProgress sx={{ width: 24, height: 24 }} />}
                          {step.status === 'pending' && <Box sx={{ width: 24, height: 24, border: '2px solid', borderColor: 'grey.400', borderRadius: '50%' }} />}
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: step.status === 'loading' ? 'bold' : 'normal',
                              color: step.status === 'error' ? 'error.main' : step.status === 'success' ? 'success.main' : 'text.primary'
                            }}
                          >
                            {step.label}
                          </Typography>
                        </Box>
                        {step.message && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              ml: 4, 
                              color: step.status === 'error' ? 'error.main' : 'text.secondary',
                              fontStyle: 'italic'
                            }}
                          >
                            {step.message}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            </StyledPaper>
          </>

        )} 
      </Container>
    </Box>
  );
}

export default GeneratorPage;
